import { useEffect, useMemo, useState } from "react";
import { useLoaderData } from "react-router-dom";
import {
  Alert,
  Badge,
  Card,
  Group,
  Loader,
  Progress,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconChartBar, IconInfoCircle } from "@tabler/icons-react";
import { useFetch } from "../../helpers/useFetch.js";
import useTitle from "../../hooks/useTitle.js";
import Page from "../../components/layout/Page.jsx";

export async function loader() {
  const res = await useFetch("pc/survey-results");
  return res;
}

const fmtAvg = (value) => (value === null || value === undefined ? "—" : value.toFixed(2));

const responsesLabel = (count) => {
  if (count === 1) return "odpověď";
  if (count >= 2 && count <= 4) return "odpovědi";
  return "odpovědí";
};

export default function SurveyResultsPage() {
  const surveys = useLoaderData()?.data ?? [];
  useTitle("Výsledky dotazníků");

  const [surveyId, setSurveyId] = useState(surveys[0]?.id ? String(surveys[0].id) : null);
  const [oddil, setOddil] = useState(null);
  const [results, setResults] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!surveyId) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (oddil) params.append("oddil", oddil);

    useFetch(`pc/survey-results/${surveyId}?${params.toString()}`).then((res) => {
      if (res?.data) {
        setResults(res.data);
        setMessage(null);
      } else {
        setResults(null);
        setMessage(res?.message ?? "Výsledky se nepodařilo načíst.");
      }
      setLoading(false);
    });
  }, [surveyId, oddil]);

  const surveyOptions = useMemo(
    () =>
      surveys.map((s) => ({
        value: String(s.id),
        label: `${s.name}${s.responsesCount ? ` (${s.responsesCount})` : ""}`,
      })),
    [surveys]
  );

  const toOptions = (options) =>
    (options ?? []).map((o) => ({ value: String(o.value), label: `${o.label} (${o.count})` }));

  const onSurveyChange = (value) => {
    setSurveyId(value);
    setOddil(null);
    setResults(null);
    setMessage(null);
  };

  return (
    <Page subtitle="Počítačový program" title="Výsledky dotazníků">
      {surveys.length === 0 ? (
        <Alert icon={<IconInfoCircle />} color="gray">
          Zatím tu není žádný dotazník.
        </Alert>
      ) : (
        <Stack gap="lg">
          <Group align="flex-end" gap="md">
            <Select
              label="Dotazník"
              placeholder="Vyber dotazník"
              data={surveyOptions}
              value={surveyId}
              onChange={onSurveyChange}
              w={320}
              maw="100%"
              searchable
            />
          </Group>

          {loading && !results ? (
            <Group justify="center" py="xl">
              <Loader />
            </Group>
          ) : message ? (
            <Alert icon={<IconInfoCircle />} color="gray">
              {message}
            </Alert>
          ) : results ? (
            <SurveyResults results={results} loading={loading} oddil={oddil} onOddil={setOddil} toOptions={toOptions} />
          ) : null}
        </Stack>
      )}
    </Page>
  );
}

function SurveyResults({ results, loading, oddil, onOddil, toOptions }) {
  const { total, totalAll, filters, questions, averages, respondents, selectedLeader, session } = results;
  const filtersActive = Boolean(oddil);

  return (
    <Stack gap="lg" pos="relative">
      {/* Hlavička s počtem odpovědí */}
      <Card withBorder radius="md" padding="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text size="sm" c="dimmed">
              {results.survey.eventType ?? ""}
              {results.survey.alias ? ` • ${results.survey.alias}` : ""}
            </Text>
            <Group gap={6} align="baseline" mt={4}>
              <Text fw={800} fz={34} lh={1}>
                {total}
              </Text>
              <Text c="dimmed">
                {responsesLabel(total)}
                {filtersActive ? ` z ${totalAll}` : ""}
              </Text>
            </Group>
            {session && (
              <Text size="sm" c="dimmed" mt={6}>
                Turnus: <b>{session.name}</b>
                {session.areaLabel ? ` • ${session.areaLabel}` : ""}
              </Text>
            )}
          </div>
          {averages.overall !== null && (
            <Badge size="lg" variant="light" leftSection={<IconChartBar size={14} />}>
              Celkový průměr: {fmtAvg(averages.overall)}
            </Badge>
          )}
        </Group>
      </Card>

      {/* Filtr oddílu (areál a turnus jsou dané vybraným turnusem uživatele) */}
      <Group align="flex-end" gap="md">
        <Select
          label="Oddíl"
          placeholder="Všechny oddíly"
          data={toOptions(filters.oddilOptions)}
          value={oddil}
          onChange={onOddil}
          clearable
          searchable
          w={220}
        />
        {oddil && selectedLeader && (
          <div>
            <Text size="xs" fw={600} c="dimmed">
              Oddílový vedoucí
            </Text>
            <Badge size="lg" variant="light" color="indigo">
              {selectedLeader}
            </Badge>
          </div>
        )}
        {loading && <Loader size="sm" />}
      </Group>

      {/* Průměry za oddíl (porovnání) */}
      {averages.byOddil.length > 0 && averages.scaleQuestions.length > 0 && (
        <Card withBorder radius="md" padding="lg">
          <Title order={4} mb="sm">
            Průměry za oddíl
          </Title>
          <ScrollArea>
            <Table striped highlightOnHover withTableBorder miw={520}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Oddíl</Table.Th>
                  <Table.Th>Oddílový vedoucí</Table.Th>
                  {averages.scaleQuestions.map((q) => (
                    <Table.Th key={q.id}>
                      <Tooltip label={q.text} multiline w={280} withArrow>
                        <Text size="xs" fw={600} style={{ maxWidth: 180, whiteSpace: "normal" }}>
                          {q.text}
                        </Text>
                      </Tooltip>
                    </Table.Th>
                  ))}
                  <Table.Th ta="center">Celkem</Table.Th>
                  <Table.Th ta="center">Odpovědí</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {averages.byOddil.map((row) => (
                  <Table.Tr key={row.oddilId}>
                    <Table.Td>{row.oddil ?? "—"}</Table.Td>
                    <Table.Td>{row.leader ?? "—"}</Table.Td>
                    {averages.scaleQuestions.map((q) => (
                      <Table.Td key={q.id} ta="center">
                        {fmtAvg(row.perQuestion[q.id])}
                      </Table.Td>
                    ))}
                    <Table.Td ta="center">
                      <Text fw={700}>{fmtAvg(row.overall)}</Text>
                    </Table.Td>
                    <Table.Td ta="center" c="dimmed">
                      {row.count}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Card>
      )}

      {/* Otázky */}
      {questions.length === 0 ? (
        <Alert icon={<IconInfoCircle />} color="gray">
          Tento dotazník nemá žádné otázky.
        </Alert>
      ) : (
        questions.map((q, index) => <QuestionBlock key={q.id} question={q} index={index + 1} />)
      )}

      {total === 0 && (
        <Alert icon={<IconInfoCircle />} color="gray">
          Zatím nikdo dotazník nevyplnil{filtersActive ? " (pro zvolené filtry)" : ""}.
        </Alert>
      )}

      {/* Respondenti */}
      {respondents.length > 0 && (
        <Card withBorder radius="md" padding="lg">
          <Title order={4} mb="sm">
            Odpovědi respondentů
          </Title>
          <ScrollArea>
            <Table striped highlightOnHover withTableBorder withColumnBorders miw={640}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Jméno</Table.Th>
                  <Table.Th>Příjmení</Table.Th>
                  <Table.Th>Oddíl</Table.Th>
                  <Table.Th>Oddílový vedoucí</Table.Th>
                  {questions.map((q) => (
                    <Table.Th key={q.id}>
                      <Tooltip label={q.text} multiline w={280} withArrow>
                        <Text size="xs" fw={600} style={{ maxWidth: 200, whiteSpace: "normal" }}>
                          {q.text}
                        </Text>
                      </Tooltip>
                    </Table.Th>
                  ))}
                  <Table.Th>Odesláno</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {respondents.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>{r.jmeno || "—"}</Table.Td>
                    <Table.Td>{r.prijmeni || "—"}</Table.Td>
                    <Table.Td>
                      <Text span c={r.oddil ? undefined : "red"}>
                        {r.oddil ?? "Nepřidělen"}
                      </Text>
                    </Table.Td>
                    <Table.Td c={r.leader ? undefined : "dimmed"}>{r.leader ?? "—"}</Table.Td>
                    {questions.map((q) => {
                      const val = r.answers?.[q.id];
                      const empty = val === "" || val === null || val === undefined;
                      return (
                        <Table.Td key={q.id}>
                          <Text
                            size="sm"
                            c={empty ? "dimmed" : undefined}
                            style={{ maxWidth: 280, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {empty ? "—" : String(val)}
                          </Text>
                        </Table.Td>
                      );
                    })}
                    <Table.Td c="dimmed" style={{ whiteSpace: "nowrap" }}>
                      {r.submitted || "—"}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Card>
      )}
    </Stack>
  );
}

function QuestionBlock({ question, index }) {
  const maxCount = question.kind === "choice" ? question.options.reduce((max, o) => Math.max(max, o.count), 0) : 0;

  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <Group align="flex-start" gap="sm" wrap="nowrap">
          <Badge circle variant="light" size="lg">
            {index}
          </Badge>
          <div>
            <Text fw={600}>{question.text}</Text>
            <Group gap="xs" mt={2}>
              <Badge size="xs" variant="light" color="gray" tt="uppercase">
                {question.type}
              </Badge>
              <Text size="xs" c="dimmed">
                {question.answered} {responsesLabel(question.answered)}
              </Text>
            </Group>
          </div>
        </Group>
        {question.isScale && question.average !== null && (
          <Badge size="lg" variant="light" color="teal">
            ⌀ {fmtAvg(question.average)}
          </Badge>
        )}
      </Group>

      {question.kind === "choice" ? (
        question.options.length === 0 ? (
          <Text c="dimmed" fs="italic" size="sm">
            Otázka nemá definované možnosti.
          </Text>
        ) : (
          <Stack gap="xs">
            {question.options.map((opt, i) => {
              const pct = question.answered > 0 ? Math.round((opt.count / question.answered) * 100) : 0;
              const isTop = opt.count === maxCount && opt.count > 0;
              return (
                <div key={i}>
                  <Group justify="space-between" gap="xs" mb={4}>
                    <Text size="sm" c={opt.other ? "dimmed" : undefined} fs={opt.other ? "italic" : undefined}>
                      {opt.title}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {opt.count} • {pct} %
                    </Text>
                  </Group>
                  <Progress value={pct} color={isTop ? "indigo" : "gray"} size="md" radius="xl" />
                </div>
              );
            })}
          </Stack>
        )
      ) : question.texts && question.texts.length > 0 ? (
        <Stack gap="xs">
          {question.texts.map((text, i) => (
            <Card key={i} withBorder radius="sm" padding="xs" bg="var(--mantine-color-default-hover)">
              <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {text}
              </Text>
            </Card>
          ))}
        </Stack>
      ) : (
        <Text c="dimmed" fs="italic" size="sm">
          Zatím bez odpovědí.
        </Text>
      )}
    </Card>
  );
}
